/**
 * this file is for serializing and deserializing the filters for the url.
 * we want to save as much space as possible. Thanks to GPT5 for creating this mess
 */

import { FilterOperators as FilterOperatorsTypes } from "@okeano-gmbh/pylo-utils";
import {
  b64ToUuid,
  decodeAdaptive,
  encodeAdaptive,
  fnv1a32,
  UUID_RE,
  uuidToB64,
} from "./url-codec";
import { FILTER_OPERATOR_META } from "./entity-filter-operators";

export type CodecFilter = {
  field: string;
  fieldType: string;
  operator: string;
  value: string;
  active: boolean;
  id: string;
};

// Packed tuple: [fieldIdx, typeIdx, opIdx, activeBit, encodedValue]
type Packed = [number, number, number, 0 | 1, string];

type Header = {
  f: string[]; // dynamic fields (not in static dict)
  t: string[]; // dynamic types (not in static dict)
};

const V = 1;

// "~" prefix marks a compacted UUID token
function encodeValue(v: string, fieldType: string, op: string): string {
  if (
    op === "isNull" ||
    op === "isNotNull" ||
    op === "isEmpty" ||
    op === "isNotEmpty" ||
    op === "isTrue" ||
    op === "isFalse"
  )
    return "";

  const trimmed = v.trim();

  // JSON array (for in/notIn)
  if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
    try {
      const arr = JSON.parse(trimmed) as unknown[];
      const enc = arr.map((x) =>
        typeof x === "string" && UUID_RE.test(x) ? "~" + uuidToB64(x) : x,
      );
      return JSON.stringify(enc);
    } catch {
      // fall through
    }
  }

  // CSV
  if (v.includes(",")) {
    return v
      .split(",")
      .map((p) => {
        const s = p.trim();
        return UUID_RE.test(s) ? "~" + uuidToB64(s) : s;
      })
      .join(",");
  }

  // Single UUID or typed uuid field
  if (fieldType === "ID" || fieldType === "uuid" || UUID_RE.test(v)) {
    return "~" + uuidToB64(v);
  }

  return v;
}

function decodeValue(v: string): string {
  if (!v) return v;
  const t = v.trim();

  if (t.startsWith("[") && t.endsWith("]")) {
    try {
      const arr = JSON.parse(t) as unknown[];
      const dec = arr.map((x) =>
        typeof x === "string" && x.startsWith("~") ? b64ToUuid(x.slice(1)) : x,
      );
      return JSON.stringify(dec);
    } catch {
      // fall through
    }
  }

  if (v.includes(",")) {
    return v
      .split(",")
      .map((p) => (p.startsWith("~") ? b64ToUuid(p.slice(1)) : p))
      .join(",");
  }

  if (v.startsWith("~")) return b64ToUuid(v.slice(1));

  return v;
}

// ---------- Stable tiny IDs (generated on parse) ----------
function makeStableId(tuple: Packed, i: number): string {
  const s = `${tuple[0]}|${tuple[1]}|${tuple[2]}|${tuple[3]}|${tuple[4]}|${i}`;
  return fnv1a32(s).toString(36);
}

// ---------- Factory (operators/fields/types are injected) ----------
export function createFilterCodec(options: {
  operators: readonly string[];
  fields?: readonly string[];
  types?: readonly string[];
}) {
  const FIELD_DICT = options.fields ?? ([] as readonly string[]);
  const TYPE_DICT = options.types ?? ([] as readonly string[]);
  const OP_DICT = options.operators;

  const FIELD_INDEX = new Map<string, number>(FIELD_DICT.map((f, i) => [f, i]));
  const TYPE_INDEX = new Map<string, number>(TYPE_DICT.map((t, i) => [t, i]));
  const OP_INDEX = new Map<string, number>(OP_DICT.map((o, i) => [o, i]));

  function serialize(filters: CodecFilter[]): string {
    if (!filters || filters.length === 0) return "";

    const dynFieldList: string[] = [];
    const dynFieldIndex = new Map<string, number>();
    const dynTypeList: string[] = [];
    const dynTypeIndex = new Map<string, number>();

    const packed: Packed[] = filters.map((f) => {
      // field idx
      let fIdx = FIELD_INDEX.get(f.field);
      if (fIdx == null) {
        let di = dynFieldIndex.get(f.field);
        if (di == null) {
          di = dynFieldList.length;
          dynFieldList.push(f.field);
          dynFieldIndex.set(f.field, di);
        }
        fIdx = FIELD_DICT.length + di;
      }

      // type idx
      let tIdx = TYPE_INDEX.get(f.fieldType);
      if (tIdx == null) {
        let di = dynTypeIndex.get(f.fieldType);
        if (di == null) {
          di = dynTypeList.length;
          dynTypeList.push(f.fieldType);
          dynTypeIndex.set(f.fieldType, di);
        }
        tIdx = TYPE_DICT.length + di;
      }

      // operator idx
      const oIdx = OP_INDEX.get(f.operator);
      if (oIdx == null) throw new Error(`Unknown operator: ${f.operator}`);

      const a = f.active ? 1 : 0;
      const v = encodeValue(f.value, f.fieldType, f.operator);

      return [fIdx, tIdx, oIdx, a, v];
    });

    const header: Header = { f: dynFieldList, t: dynTypeList };
    const payload = JSON.stringify([V, header, packed]);
    return encodeAdaptive(payload);
  }

  function parse(s: string): CodecFilter[] {
    if (!s) return [];
    const json = decodeAdaptive(s);
    const [ver, header, packed] = JSON.parse(json) as [number, Header, Packed[]];
    if (ver !== V) return [];

    const dynFields = header?.f ?? [];
    const dynTypes = header?.t ?? [];

    const toField = (idx: number): string => {
      if (idx < FIELD_DICT.length) return FIELD_DICT[idx];
      const di = idx - FIELD_DICT.length;
      if (di < 0 || di >= dynFields.length) throw new Error("Bad field idx");
      return dynFields[di];
    };

    const toType = (idx: number): string => {
      if (idx < TYPE_DICT.length) return TYPE_DICT[idx];
      const di = idx - TYPE_DICT.length;
      if (di < 0 || di >= dynTypes.length) throw new Error("Bad type idx");
      return dynTypes[di];
    };

    return packed.map((t, i) => {
      const [fIdx, tIdx, oIdx, a, v] = t;
      return {
        field: toField(fIdx),
        fieldType: toType(tIdx),
        operator: OP_DICT[oIdx]!,
        value: decodeValue(v),
        active: a === 1,
        id: makeStableId(t, i),
      };
    });
  }

  return { serialize, parse };
}

const KNOWN_TYPES = Array.from(new Set(FILTER_OPERATOR_META.flatMap((o) => o.activeFor ?? [])));

const entityCodec = createFilterCodec({
  operators: FilterOperatorsTypes,
  fields: [],
  types: KNOWN_TYPES,
});

export const serializeEntityFilters = entityCodec.serialize;
export const parseEntityFilters = entityCodec.parse;
